import {
  firstEvalValue,
  useEvaluationMetric,
  useEvaluationPrimaryKeys,
} from '@/features/forecasts/hooks/useForecastEvaluation';
import type { EvalMetricKey, EvalModel, EvalWindowKey } from '@/features/forecasts/types';
import { cls } from '@/utils/cls';

const fmtPct = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
const fmtNum = (v: number | null) => (v == null ? '—' : v.toFixed(2));

type Column = {
  id: string;
  label: string;
  sublabel?: string;
  metric: EvalMetricKey;
  window: EvalWindowKey;
  format: (v: number | null) => string;
};

const COLUMNS: Column[] = [
  { id: 'wapeWeek', label: '1 Week WAPE', sublabel: '(n=13, lead-time 1w, span 1w)', metric: 'wape', window: 'week', format: fmtPct }, // prettier-ignore
  { id: 'wapeMonth', label: '1 Month WAPE', sublabel: '(n=13, lead-time 1w, span 1m)', metric: 'wape', window: 'month', format: fmtPct }, // prettier-ignore
  { id: 'wapeQuarter', label: '3 Month WAPE', sublabel: '(n=13, lead-time 1w, span 3m)', metric: 'wape', window: 'quarter', format: fmtPct }, // prettier-ignore

  { id: 'bias', label: 'Net Bias', sublabel: '(n=13, lead-time 1w, span 3m)', metric: 'bias', window: 'quarter', format: fmtPct }, // prettier-ignore
  {
    id: 'pinball',
    label: 'Pinball Loss',
    sublabel: '(n=13, ETS = mean)',
    metric: 'pinball',
    window: 'month',
    format: fmtNum,
  },
  { id: 'coverage', label: 'Coverage P50', sublabel: '(n=13, ETS = mean)', metric: 'coverage', window: 'month', format: fmtPct }, // prettier-ignore
];

type CellState = { value: number | null; isLoading: boolean; isError: boolean };

// Fetch both models' value for one column in a single hook, so the reduction row
// (ETS − BFG) can be derived. COLUMNS is a fixed-length constant, so calling this
// once per column at the top of the table keeps hook order stable.
function useColumnValues(column: Column): { bfg: CellState; incumbent: CellState } {
  const bfg = useEvaluationMetric({ model: 'bfg', metric: column.metric, window: column.window });
  const incumbent = useEvaluationMetric({ model: 'incumbent', metric: column.metric, window: column.window });
  return {
    bfg: { value: firstEvalValue(bfg.data?.results), isLoading: bfg.isLoading, isError: bfg.isError },
    incumbent: {
      value: firstEvalValue(incumbent.data?.results),
      isLoading: incumbent.isLoading,
      isError: incumbent.isError,
    },
  };
}

function Cell({ state, format }: { state: CellState; format: (v: number | null) => string }) {
  return (
    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900">
      {state.isLoading ? (
        <span className="text-gray-300">…</span>
      ) : state.isError ? (
        <span className="text-gray-300">—</span>
      ) : (
        format(state.value)
      )}
    </td>
  );
}

export function ForecastEvaluationTable() {
  const primaryKeys = useEvaluationPrimaryKeys();
  const hasSelection = Object.keys(primaryKeys).length > 0;

  // One query pair (BFG + ETS) per column. Explicit calls keep hook order
  // unconditionally stable — COLUMNS has exactly 6 entries.
  const columnValues = [
    useColumnValues(COLUMNS[0]),
    useColumnValues(COLUMNS[1]),
    useColumnValues(COLUMNS[2]),
    useColumnValues(COLUMNS[3]),
    useColumnValues(COLUMNS[4]),
    useColumnValues(COLUMNS[5]),
  ];

  const rows: Array<{ model: EvalModel | 'reduction'; label: string }> = [
    { model: 'incumbent', label: 'Incumbent (ETS)' },
    { model: 'bfg', label: 'Foundation Forecasting (BFG)' },
    { model: 'reduction', label: 'Reduction in error' },
  ];

  return (
    <div className="space-y-4">
      {/* Metric grid */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Model
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={col.id}
                  className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  {col.label}
                  {col.sublabel && <div className="font-normal normal-case text-gray-400">{col.sublabel}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!hasSelection ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-4 py-6 text-center text-sm text-gray-400">
                  Select a product or customer to load evaluation metrics
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.model} className={cls(row.model === 'reduction' && 'bg-teal-50/50 font-medium')}>
                  <td className="px-4 py-3 text-left text-sm font-medium text-gray-900">{row.label}</td>
                  {COLUMNS.map((col, i) => {
                    const { bfg, incumbent } = columnValues[i];
                    if (row.model === 'incumbent') return <Cell key={col.id} state={incumbent} format={col.format} />;
                    if (row.model === 'bfg') return <Cell key={col.id} state={bfg} format={col.format} />;

                    // Reduction in error: positive = BFG better than ETS.
                    //
                    // Bias is signed — a forecast can be biased high or low, so
                    // "better" means closer to zero. The reduction is the drop in
                    // absolute bias, |ETS %| − |BFG %|.
                    //
                    // Every other metric is lower-is-better, so the reduction is
                    // the relative improvement (ETS − BFG) / ETS.
                    const both = !bfg.isLoading && !incumbent.isLoading;
                    const hasValues = incumbent.value != null && bfg.value != null;
                    const reduction = !hasValues
                      ? null
                      : col.metric === 'bias'
                        ? Math.abs(incumbent.value!) - Math.abs(bfg.value!)
                        : incumbent.value !== 0
                          ? (incumbent.value! - bfg.value!) / incumbent.value!
                          : null;
                    // Green only when BFG genuinely improves on ETS (positive
                    // reduction); red when BFG is worse, neutral when unavailable.
                    const tone =
                      reduction == null
                        ? 'text-gray-400'
                        : reduction > 0
                          ? 'text-teal-700'
                          : reduction < 0
                            ? 'text-rose-600'
                            : 'text-gray-600';
                    return (
                      <td key={col.id} className={cls('px-4 py-3 text-right text-sm tabular-nums', tone)}>
                        {!both ? <span className="text-gray-300">…</span> : fmtPct(reduction)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
